'use client';

import { getCookies } from 'cookies-next';
import {
  ReadonlyURLSearchParams,
  usePathname,
  useRouter,
  useSearchParams,
} from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import {
  maxAge,
  minAge,
  vulnerableCountryIsoCodes,
} from '@sola/contentful-model/lib/sac-values.js';
import { childSearch, getRandomChildren } from '@sola/proxy-sdk/children.js';
import { ChildAvailabilityItem } from '@sola/schema/dotcom/children/child.js';
import { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.js';
import { SacFilterProps } from '@sola/schema/lib/schema.js';

import { SacFiltersEnum } from '../../utils/query-params';

// TODO: Refactor to use useSacQueryParams hook instead of useSearchParams + sacParamKeys.
// This would eliminate the need for manual param parsing and provide type-safe access to filter values.
const sacParamKeys = {
  filters: 'filters',
  gender: 'gender',
  ageMin: 'ageMin',
  ageMax: 'ageMax',
  countries: 'countries',
  birthDay: 'birthDay',
  birthMonth: 'birthMonth',
} as const;

const defaultLimit = 12;
const numberOfShowMoreRichMix = 10;

const parseSearchCriteria = (
  filters: SacFilterProps,
  params?: ReadonlyURLSearchParams,
) => {
  const {
    gender: filterGender,
    ageMin: filterAgeMin,
    ageMax: filterAgeMax,
    countries: filterCountries,
    hiv,
    orphan,
    disability: filterDisability,
    highlyVulnerable,
  } = filters;

  const queryFilter = params?.get(sacParamKeys.filters);
  const gender =
    (params?.get(sacParamKeys.gender) as ChildGenderEnum) ||
    (filterGender as ChildGenderEnum) ||
    undefined;
  const ageMin =
    Number(params?.get(sacParamKeys.ageMin)) || filterAgeMin || undefined;
  const ageMax =
    Number(params?.get(sacParamKeys.ageMax)) || filterAgeMax || undefined;
  const selectedCountries = params?.get(sacParamKeys.countries)
    ? [params.get(sacParamKeys.countries) || '']
    : filterCountries;
  // TODO: once we settle on the Contentful schema, we'll know more how to handle this
  const birthDay = params?.get(sacParamKeys.birthDay);
  const birthMonth = params?.get(sacParamKeys.birthMonth);
  // Only set the booleans to true or undefined (although a filter could possibly set to false)
  const isOrphan = params
    ?.get(sacParamKeys.filters)
    ?.includes(SacFiltersEnum.enum.orphanedChild)
    ? true
    : orphan;
  const isInHivAffectedArea = queryFilter?.includes(
    SacFiltersEnum.enum.affectedHiv,
  )
    ? true
    : hiv;
  const disability = queryFilter?.includes(SacFiltersEnum.enum.hasDisabilities)
    ? true
    : filterDisability;
  const isVulnerable = queryFilter?.includes(
    SacFiltersEnum.enum.vulnerableExploitation,
  )
    ? true
    : highlyVulnerable;

  let countries = selectedCountries;
  // Highly vulnerable is just a list of countries...
  if (isVulnerable) {
    const hvc = vulnerableCountryIsoCodes as readonly string[];
    countries = selectedCountries?.length
      ? selectedCountries.filter((c) => hvc.includes(c))
      : [...vulnerableCountryIsoCodes];
    if (!countries?.length) {
      // Did they have a country selected that isn't one of the HVC countries - return to the selected country
      countries = selectedCountries;
      // TOOD: inform the user of the conflict? update the interface to reflect this?
    }
  }

  const hasCriteria = !!(
    gender ||
    (ageMin && ageMax
      ? ageMin !== minAge || ageMax !== maxAge
      : ageMin || ageMax) ||
    countries?.length ||
    birthDay ||
    birthMonth ||
    isInHivAffectedArea ||
    disability ||
    isOrphan
  );

  return {
    hasCriteria,
    gender,
    ageMin,
    ageMax,
    countries,
    birthDay,
    birthMonth,
    disability,
    isOrphan,
    isInHivAffectedArea,
  };
};

export const useSacChildren = (
  filters: SacFilterProps,
  allowShowMore: boolean,
) => {
  const [children, setChildren] = useState<ChildAvailabilityItem[]>([]);
  const [richMixChildren, setRichMixChildren] = useState<
    ChildAvailabilityItem[] | undefined
  >();
  const params = useSearchParams();

  const { limit = defaultLimit } = filters;
  const {
    hasCriteria,
    gender,
    ageMin,
    ageMax,
    countries,
    birthDay,
    birthMonth,
    disability,
    isOrphan,
    isInHivAffectedArea,
  } = useMemo(() => parseSearchCriteria(filters, params), [params]);

  const [isLoading, setIsLoading] = useState(true);
  const [viewMoreCount, setViewMoreCount] = useState(0);
  const [showRichMix, setShowRichMix] = useState(hasCriteria);

  const pathname = usePathname();
  const router = useRouter();
  const cookieStore = getCookies();

  const numberOfRichMixChildrenFetch = limit * numberOfShowMoreRichMix;

  const skip = viewMoreCount * limit;

  const resetFilters = () => router.push(pathname);

  const [showLoadMoreButton, setShowLoadMoreButton] = useState<boolean>(
    allowShowMore && viewMoreCount < numberOfShowMoreRichMix - 1,
  );

  useEffect(() => {
    setIsLoading(true);
    if (hasCriteria) {
      setShowRichMix(false);
    } else {
      setShowRichMix(true);
    }
    setViewMoreCount(0);
    setShowLoadMoreButton(true);
    setRichMixChildren([]);
    setChildren([]);
    setIsLoading(false);
  }, [params]);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      if (showRichMix || !hasCriteria) {
        return;
      }
      if (!skip) {
        setIsLoading(true);
      }

      const results = await childSearch({
        take: limit,
        skip,
        gender,
        ageMin,
        ageMax,
        countries,
        // TODO: once we settle on the Contentful schema, we'll know more how to handle this
        birthDay,
        birthMonth,
        isOrphan,
        isInHivAffectedArea,
        disability,
        referrer: cookieStore.referrer,

        // Todo: Add highly vulnerable entry in the Api
        // isVulnerable:
        //   params
        //     ?.get(sacParamKeys.filters)
        // ?.includes(SacFiltersEnum.enum.vulnerableExploitation) ?? highlyVulnerable,
        // todo: Vulnerable to Exploitation when supported by API
      });

      if (!cancelled) {
        setShowLoadMoreButton(
          allowShowMore &&
            !!results?.children &&
            results.children.length >= limit,
        );

        if (skip) {
          setChildren((c) => [...c, ...(results?.children || [])]);
        } else {
          setChildren(() => [...(results?.children || [])]);
        }
      }
      if (!skip) {
        setIsLoading(false);
      }
    };

    fetchData();

    return (): void => {
      cancelled = true;
    };
  }, [
    limit,
    skip,
    params,
    gender,
    ageMax,
    ageMin,
    countries,
    isInHivAffectedArea,
    disability,
    isOrphan,
    showRichMix,
  ]);

  useEffect(() => {
    if (!showRichMix || hasCriteria) {
      return;
    }
    const fetchData = async () => {
      setIsLoading(true);
      const results = await getRandomChildren(numberOfRichMixChildrenFetch);
      setRichMixChildren(results?.children);
      setIsLoading(false);
    };

    fetchData();
  }, [numberOfRichMixChildrenFetch, showRichMix]);

  useEffect(() => {
    if (showRichMix) {
      setShowLoadMoreButton(
        allowShowMore && viewMoreCount < numberOfShowMoreRichMix - 1,
      );
    }
  }, [viewMoreCount]);

  return {
    isLoading,
    children,
    richMixChildren,
    showRichMix,
    showLoadMoreButton,
    viewMoreCount,
    setViewMoreCount,
    resetFilters,
    limit,
  };
};
